import { useEffect, useRef, useState } from "react";
import { needsSmallSizeConfirm } from "../functions/dragResize";

interface PendingSize {
  widthMm: number;
  heightMm: number;
}

/**
 * Flag REACT_APP_LOGO_DRAG_RESIZE: commits a dragged size through the SAME
 * chain as the "Change size" button (form -> onResize), but only once the
 * form has actually arrived in a render. The view-models' handleResize reads
 * logoForm from their own render closure, so calling onResize in the same
 * event as the last form update would send the PREVIOUS size. The returned
 * function stores the size, applies it to the form and bumps a tick; the
 * effect then fires in the render where both landed (React batches the two
 * state updates) and calls onResize - or the small-size confirmation, as
 * the button does for <= 30x30 mm - with the final mm in scope. A pending
 * size the form did not take is dropped, never replayed later.
 */
export function useDragResizeCommit(
  form: { widthMm: number; heightMm: number },
  applySize: (widthMm: number, heightMm: number) => void,
  onResize: () => void,
  openSmallSizeConfirm: () => void
): (widthMm: number, heightMm: number) => void {
  const pendingRef = useRef<PendingSize | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (
      pending.widthMm !== form.widthMm ||
      pending.heightMm !== form.heightMm
    ) {
      return;
    }
    if (needsSmallSizeConfirm(pending.widthMm, pending.heightMm)) {
      openSmallSizeConfirm();
    } else {
      onResize();
    }
  }, [tick, form.widthMm, form.heightMm]);

  return (widthMm: number, heightMm: number) => {
    pendingRef.current = { widthMm, heightMm };
    applySize(widthMm, heightMm);
    setTick((t) => t + 1);
  };
}
